/**
 * Billing Service
 *
 * API service for billing operations.
 */

import type { BillingProfileInput, CompanyBillingDetailsInput } from '@tonnie/schemas';
import type { InvoiceDocumentKind, InvoiceKind, InvoiceStatus, PlanFeature } from '@tonnie/types';
import { TonnieClient } from '../client/base';
import type { JourneyFundingSource } from './journey.service';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

export interface UsageCounter {
  used: number;
  /** `null` = unlimited. */
  limit: number | null;
}

export interface SponsoredTalent {
  id: string;
  talentId: string;
  /** The journey the sponsorship funds — a talent may hold several. */
  journeyInstanceId: string;
  talentName: string;
  talentEmail: string;
  route: { from: string; to: string };
  programId: string | null;
  program?: string;
  cohortId: string | null;
  cohort?: string;
  /** One-time payment amount (column name is legacy from when it was recurring). */
  amount: number;
  currency: string;
  status:
    | 'pending'
    | 'awaiting_talent_acceptance'
    | 'active'
    | 'cancelling'
    | 'cancelled'
    | 'past_due'
    | 'failed'
    | 'talent_declined'
    | 'refunded'
    /** The 12-month window ran its course — no refund; the drawer keeps its history. */
    | 'completed';
  stage: string;
  progress: number;
  familyAccess: boolean;
  startDate: string | null;
  endDate: string | null;
  monthsRemaining: number;
  stripeSubscriptionId: string | null;
}

export interface BillingSummary {
  /** Recurring charges in the current period (Enterprise subscription only). 0
   *  for a Free tenant — sponsorships are one-time, not recurring (BL-064). */
  currentPeriodCharges: number;
  /** Recurring next-invoice estimate (Enterprise subscription only). 0 when
   *  there's no recurring subscription. */
  nextInvoiceEstimate: number;
  billingPeriod: string;
  nextInvoiceDate: string | null;
  // ─── BL-064: one-time sponsorship facts, kept distinct from the recurring
  // forecast so the UI never presents a paid one-time charge as an upcoming
  // invoice. Optional for backward compatibility with older API responses.
  /** True only when an active recurring (Enterprise) subscription exists. */
  hasRecurringSubscription?: boolean;
  /** Sum of active/awaiting one-time sponsorship charges already paid (€). */
  sponsorshipsOneTimeTotal?: number;
  /** Count of active/awaiting sponsorships. */
  activeSponsorshipCount?: number;
  /** Fixed access window granted per sponsorship, in months (12). */
  sponsorshipAccessMonths?: number;
}

export interface Subscription {
  /** Overall status: 'free' when no subscription, or the primary subscription status */
  status: 'free' | 'active' | 'cancelled' | 'expired' | 'past_due' | 'none';
  /** Simplified plan label */
  plan: 'free' | 'pro' | 'journey' | 'enterprise';
  /** Full plan details (null if free/no subscription) */
  planDetails: {
    id: string;
    code: string;
    name: string;
    description: string | null;
    price: number;
    currency: string;
    interval: string;
    features: PlanFeature[];
    /** BL-065: beta-decorated price (currentPrice = €1 in beta). Prefer this
     *  over raw `price` for any display. Optional for older API responses. */
    pricing?: PricingDto;
  } | null;
  /** Whether the user has an active Pro subscription (monthly or yearly) */
  proSubscriptionActive: boolean;
  /** Whether the user has an active Family Add-on subscription */
  familyAddonActive: boolean;
  /**
   * SERVER-AUTHORITATIVE Enterprise entitlement — the same
   * `BillingEntitlementsService.isEnterprise` the API guards every
   * Enterprise-only capability with (e.g. the Talent Pool).
   *
   * Always prefer this over re-deriving from `plan`/`status` on the client. The
   * two used to be computed differently — the client on the plan CODE over
   * `active` only, the server on `metadata.tier` over `active` **and**
   * `past_due` — so a tenant in dunning grace saw a locked UI while every API
   * call succeeded. Every subscription response carries it.
   */
  isEnterprise: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;

  // ─── B2B enrichment (only present for business tenants) ──────────────
  /** Discriminator: present + `'b2b'` on business tenants. Undefined on B2C. */
  tenantType?: 'b2b';
  usage?: {
    seats: UsageCounter;
    programs: UsageCounter;
    cohorts: UsageCounter;
    vetting: UsageCounter;
    copilotQueries: UsageCounter;
  };
  billingSummary?: BillingSummary;
  /** True when the backend can mint a Stripe Customer Portal URL for this tenant. */
  stripeCustomerPortalAvailable?: boolean;
}

/**
 * Server-owned discount decoration (BL-022). FEs render strikethrough
 * `listPrice` + `currentPrice` + `t(discount.labelKey)` — never compute
 * prices client-side.
 */
export interface PricingDto {
  listPrice: number;
  currentPrice: number;
  discount: {
    mode: 'fixed_price' | 'percentage';
    percentOff?: number;
    labelKey: string;
  } | null;
  disclaimerKey?: string;
}

export interface JourneyPricing {
  unlock: PricingDto;
  renewal: PricingDto;
  currency: string;
  /** Months of access a one-shot unlock grants. */
  unlockMonths: number;
}

export interface CancelSubscriptionResult {
  success: boolean;
  /** True when the subscription runs to `endsAt`, the end of the paid period. */
  cancelAtPeriodEnd: boolean;
  /** ISO date the subscription — and what it grants — ends. */
  endsAt: string;
}

export interface Plan {
  id: string;
  code: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  features: PlanFeature[];
  /** BL-022 discount decoration — present on all plans endpoints. */
  pricing?: PricingDto;
  /**
   * What this yearly plan saves over twelve months of its monthly plan, at the
   * prices actually charged — measured by the server, rendered as-is. Null for
   * every other plan, and for a yearly plan that saves nothing.
   */
  annualSavings: number | null;
}

/**
 * A sponsorship checkout is for an EXISTING relationship — an accepted
 * invitation or a journey the company owns — and the server resolves the
 * journey, its route and its cohort from it. There is no direct-from-Pool
 * sponsorship: a Pool talent is invited first (N6).
 */
export interface CreateSponsorshipCheckoutInput {
  talentId: string;
  /** The journey of the relationship being sponsored — the card the admin clicked. */
  journeyId: string;
  /**
   * Sponsor a journey of this company's own on `journeyId`'s route (D-D) — the
   * way forward when the API answers `JOURNEY_SHARED_WITH_ANOTHER_COMPANY`.
   */
  dedicatedJourney?: boolean;
  familyAccess?: boolean;
  successUrl: string;
  cancelUrl: string;
  /**
   * Admin-composed activation message (Sponsor Modal v2, 2026-Q3).
   * Persisted onto the sponsorship row at creation; the Stripe webhook
   * reads it back to auto-dispatch the activation email after payment
   * confirmation. Capped at 2000 chars to match the backend Zod schema.
   */
  customMessage?: string;
}

export interface SponsorshipCheckoutResult {
  sponsorshipId: string;
  sessionId: string;
  url: string;
  amount: number;
  currency: string;
  planCode: string;
}

/**
 * Citizen-side: details of a pending sponsorship offer surfaced in
 * journey-web's `SponsorshipOfferDialog`. Returned by
 * `GET /api/billing/sponsorships/:id/offer`.
 */
export interface SponsorshipOfferDetails {
  sponsorship: {
    id: string;
    tenantId: string;
    journeyInstanceId: string;
    status: string;
    amount: string | number;
    currency: string;
    talentAcceptedAt: string | null;
    talentDeclinedAt: string | null;
    /**
     * D-D — the offer is for a journey of the company's OWN on `journey`'s
     * route: accepting creates it, and `journey` stays with the other company
     * the citizen shares it with.
     */
    dedicatedJourney: boolean;
  };
  talentId: string;
  company: { name: string; logo: string | null };
  /** When the citizen's "Not now" lapses; null when the offer was not put off. */
  deferredUntil: string | null;
  /** The journey the offer names — told apart from same-route ones by its name and start. */
  journey: {
    id: string;
    originCountry: string | null;
    destinationCountry: string;
    label: string | null;
    /** ISO. */
    startedAt: string;
  };
  /**
   * The journey is one the talent already paid for: accepting hands it to the
   * company. `pro` — Pro keeps covering their other journeys, and this one
   * returns to it if the company cancels; `paid` — the unused part of their
   * unlock is refunded (`refund`, `null` when nothing is left). `null` for any
   * other journey, and once the offer is answered.
   */
  takeover:
    | { kind: 'pro' }
    | { kind: 'paid'; refund: { amountCents: number; currency: string } | null }
    | null;
}

/**
 * Citizen-side: a sponsorship offer waiting for the signed-in citizen's answer
 * — listed beside their invitations (bell, Inbox, Home). Returned by
 * `GET /api/billing/sponsorships/offers/mine`.
 */
export interface MySponsorshipOffer {
  id: string;
  companyName: string;
  companyLogo: string | null;
  /** The journey the offer names (with `dedicatedJourney`: the one it was bought from). */
  journey: {
    id: string;
    originCountry: string | null;
    destinationCountry: string;
    label: string | null;
  };
  /** Accepting creates a journey of the company's own on this route (D-D). */
  dedicatedJourney: boolean;
  /** The offer takes over a journey the citizen paid for: declining leaves it as it is. */
  takesOverOwnJourney: boolean;
  /** ISO — when the offer was sent. */
  offeredAt: string;
  /** When "Not now" lapses; null when it was not put off (or that lapsed). */
  deferredUntil: string | null;
}

export interface PortalSession {
  url: string;
}

/** One page of the invoice history — the API reads `limit` and `offset`. */
export interface InvoicePage {
  limit: number;
  offset: number;
}

export interface Invoice {
  id: string;
  /** Stripe's own invoice number — the number to show. */
  number: string | null;
  /**
   * The reference an invoice from before 3 Oct 2026 was given (`PAY-…` /
   * `SUB-…`) — shown, labelled as a payment reference, when Stripe issued no
   * number. Never a number.
   */
  paymentReference: string | null;
  /**
   * MAJOR units of `currency` — euros, NOT cents (`19.99`, never `1999`).
   * `billing_invoices.amount` is `decimal(10,2)`; every writer divides Stripe's
   * cents by 100 before storing. Render it with `useLocale().formatCurrency`
   * as it comes. The mobile Billing screen once divided by 100 again and showed
   * a €19.99 invoice as "€0.20" (L6 round 3).
   */
  amount: number;
  /** Major units refunded so far; 0 = none. A refund draws a badge with this amount. */
  refundedAmount: number;
  /** ISO 4217, lower case as Stripe writes it — upper-case it for Intl. */
  currency: string;
  status: InvoiceStatus;
  createdAt: string;
  paidAt: string | null;
  /**
   * What "View" opens: the CERTIFIED invoice-receipt once it is issued; before
   * that, Stripe's invoice, or the receipt of a one-off payment. `null` =
   * nothing to open. The link itself is never in the list —
   * `useOpenInvoiceDocument` mints a fresh one per open (stored links expire).
   */
  documentKind: InvoiceDocumentKind | null;
  /** The certified invoice-receipt's number, once issued — shown before Stripe's. */
  fiscalNumber: string | null;
  /** What the invoice was for — draw it through `INVOICE_KIND_LABEL_KEY`. */
  kind: InvoiceKind;
  journeyId?: string | null;
  journeyOrigin?: string | null;
  journeyDestination?: string | null;
  journeyScenario?: string | null;
}

export type { InvoiceDocumentKind } from '@tonnie/types';

/** A citizen's billing profile — who their certified invoices are addressed to. */
export interface BillingProfile {
  legalName: string;
  /** Stored without the VAT prefix (`515396010`). */
  taxId: string | null;
  /** ISO 3166-1 alpha-2 — the billing country, which decides the VAT. */
  country: string;
  addressLine1: string;
  addressLine2: string | null;
  postalCode: string;
  city: string;
  updatedAt: string;
}

/** A company's billing details — what its certified invoices are addressed to. */
export interface CompanyBillingDetails {
  legalName: string | null;
  taxId: string | null;
  country: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  postalCode: string | null;
  city: string | null;
}

/**
 * Whether a checkout will be refused for want of the certified invoice's
 * recipient — and which fields are missing (`BillingProfile` field names for a
 * person; `legalName`, `taxId`, `country`, `addressLine1`, `postalCode`, `city`
 * for a company).
 */
export interface FiscalReadiness {
  required: boolean;
  holder: 'company' | 'person';
  missing: string[];
}

export type FiscalAlert =
  | { kind: 'failed_documents'; count: number }
  | { kind: 'next_year_series_missing'; year: number }
  | { kind: 'eu_consumer_threshold'; year: number; amount: number; threshold: number };

export interface FiscalAttentionRow {
  reason: 'failed' | 'pending' | 'credit_note_pending';
  invoiceId: string;
  tenantId: string;
  tenantName: string;
  kind: InvoiceKind;
  amount: number;
  refundedAmount: number;
  currency: string;
  paidAt: string | null;
  documentNumber: string | null;
  error: string | null;
  attempts: number;
}

/** The Tonnie team's certified-invoicing reconciliation, across every tenant. */
export interface FiscalReconciliation {
  enabled: boolean;
  provider: 'jasmin' | 'simulated';
  config: { ok: boolean; problem: string | null; issueFrom: string | null };
  counts: {
    issued: number;
    pending: number;
    failed: number;
    creditNotesPending: number;
    retroactive: number;
  };
  alerts: FiscalAlert[];
  attention: FiscalAttentionRow[];
}

/** Result of `GET /api/billing/coupons/validate` (BL-213). */
export interface ValidatedCouponResult {
  valid: boolean;
  code: string;
  percentOff: number;
  affiliateName: string;
}

export interface CheckoutSession {
  /** Null when the backend detected an existing subscription and applied an in-place plan change. */
  sessionId: string | null;
  /** Null when `updated=true` — frontend should not redirect. */
  url: string | null;
  /** True when an existing Stripe subscription was updated rather than creating a new Checkout session. */
  updated?: boolean;
  plan?: {
    id: string;
    name: string;
    price: number;
  };
}

export interface JourneyUnlockResult {
  sessionId: string;
  url: string;
  journey: {
    id: string;
    originCountry: string;
    destinationCountry: string;
    scenario: string;
  };
  amount: number;
  currency: string;
}

export interface ProJourneyUnlockResult {
  success: boolean;
  journeyId: string;
  /**
   * The journey's funding after the call: `'self_pro'` when a basic journey
   * was unlocked; its OWN funding (a paid unlock, a sponsorship) when it was
   * already on the full tier — Pro leaves those as they are, `alreadyUnlocked`.
   */
  fundingSource: JourneyFundingSource;
  tier: 'full';
  expiresAt?: string | null;
  alreadyUnlocked?: boolean;
}

export interface JourneyRenewalResult {
  sessionId: string;
  url: string;
  journey: {
    id: string;
    originCountry: string;
    destinationCountry: string;
    scenario: string;
  };
  amount: number;
  currency: string;
  newExpiresAt: string;
}

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class BillingService {
  constructor(private client: TonnieClient) {}

  // ─────────────────────────────────────────────────────────────────────
  // SUBSCRIPTION
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Get current subscription
   */
  async getSubscription(): Promise<Subscription> {
    return this.client.get<Subscription>('/api/billing/subscription');
  }

  /**
   * Get available plans
   */
  async getPlans(): Promise<Plan[]> {
    return this.client.get<Plan[]>('/api/billing/plans');
  }

  /**
   * Journey unlock/renewal pricing (server-owned, discount-decorated).
   */
  async getJourneyPricing(): Promise<JourneyPricing> {
    return this.client.get<JourneyPricing>('/api/billing/journey-pricing');
  }

  /**
   * Create checkout session for plan upgrade
   */
  async createCheckout(params: {
    planId: string;
    successUrl: string;
    cancelUrl: string;
    /** Optional affiliate coupon code (BL-213). */
    couponCode?: string;
  }): Promise<CheckoutSession> {
    return this.client.post<CheckoutSession>('/api/billing/checkout', params);
  }

  /**
   * Validate an affiliate coupon code before checkout (BL-213). Public —
   * returns only non-sensitive fields (code, percentOff, affiliateName). The
   * backend throws typed errors (404 not found, 410 expired, 409 over-limit)
   * which surface via the SDK error envelope.
   */
  async validateCoupon(code: string, product?: string): Promise<ValidatedCouponResult> {
    const params = new URLSearchParams({ code });
    if (product) params.set('product', product);
    return this.client.get<ValidatedCouponResult>(
      `/api/billing/coupons/validate?${params.toString()}`
    );
  }

  /**
   * Cancel subscription
   */
  /**
   * Cancel the caller's subscription at the end of the period already paid
   * for: it stays active until `endsAt`. `cancelAtPeriodEnd` is false only for
   * a subscription Stripe never billed, which ends at once.
   */
  async cancelSubscription(): Promise<CancelSubscriptionResult> {
    return this.client.post<CancelSubscriptionResult>('/api/billing/subscription/cancel');
  }

  /**
   * Mint a Stripe Customer Portal session URL the user can open in a new
   * tab. The URL expires after a few minutes and is single-use.
   */
  async createPortalSession(returnUrl: string): Promise<PortalSession> {
    return this.client.post<PortalSession>('/api/billing/portal-session', { returnUrl });
  }

  // ─────────────────────────────────────────────────────────────────────
  // SPONSORSHIPS (B2B)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * List all pending/active/cancelling talent sponsorships for the current tenant.
   */
  async getSponsorships(): Promise<SponsoredTalent[]> {
    return this.client.get<SponsoredTalent[]>('/api/billing/sponsorships');
  }

  /**
   * Create a Stripe Checkout session to sponsor a talent. The frontend
   * should redirect to `result.url` to complete payment.
   */
  async createSponsorshipCheckout(
    input: CreateSponsorshipCheckoutInput
  ): Promise<SponsorshipCheckoutResult> {
    return this.client.post<SponsorshipCheckoutResult>('/api/billing/sponsorships', input);
  }

  /**
   * Toggle the family-access flag on an active sponsorship.
   */
  async toggleSponsorshipFamilyAccess(
    sponsorshipId: string,
    enabled: boolean
  ): Promise<{ success: boolean; familyAccess: boolean }> {
    return this.client.patch<{ success: boolean; familyAccess: boolean }>(
      `/api/billing/sponsorships/${sponsorshipId}/family`,
      { enabled }
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // SPONSORSHIP LIFECYCLE (2026-Q2 redesign)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Admin: dispatch the activation email to the talent. Called from
   * `SponsorReturnPage` after Stripe Checkout completes.
   */
  async dispatchSponsorshipActivation(
    sponsorshipId: string,
    input: { customMessage?: string; force?: boolean }
  ): Promise<{ sendId: string; alreadyDispatched?: boolean }> {
    return this.client.post<{ sendId: string; alreadyDispatched?: boolean }>(
      `/api/billing/sponsorships/${sponsorshipId}/dispatch-activation`,
      input
    );
  }

  /**
   * Admin: preview the refund amount that would be issued if this
   * active sponsorship were cancelled now. Pro-rated by months
   * remaining.
   */
  async getSponsorshipRefundPreview(sponsorshipId: string): Promise<{
    refundAmountCents: number;
    refundAmountMajor: number;
    monthsRemaining: number;
    durationMonths: number;
    currency: string;
    refundable: boolean;
  }> {
    return this.client.get(`/api/billing/sponsorships/${sponsorshipId}/refund-preview`);
  }

  /**
   * Admin: cancel an active sponsorship (pro-rated refund).
   */
  async cancelSponsorship(
    sponsorshipId: string,
    input: { reason?: string } = {}
  ): Promise<{
    sponsorship: { id: string; status: string };
    /** `null` when there was nothing to refund (a zero-amount sponsorship). */
    refundId: string | null;
    refundAmountCents: number;
  }> {
    return this.client.post(`/api/billing/sponsorships/${sponsorshipId}/cancel`, input);
  }

  /**
   * Admin: abandon a sponsorship before the talent has accepted (full refund).
   */
  async abandonSponsorship(
    sponsorshipId: string,
    input: { reason?: string } = {}
  ): Promise<{
    sponsorship: { id: string; status: string };
    /** `null` when there was nothing to refund (a zero-amount sponsorship). */
    refundId: string | null;
    refundAmountCents: number;
  }> {
    return this.client.post(`/api/billing/sponsorships/${sponsorshipId}/abandon`, input);
  }

  // ─────────────────────────────────────────────────────────────────────
  // SPONSORSHIP — CITIZEN-SIDE
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Citizen: the sponsorship offers waiting for my answer, newest first.
   * `includeDeferred` adds the ones put off with "Not now".
   */
  async mySponsorshipOffers(
    opts: { includeDeferred?: boolean } = {}
  ): Promise<MySponsorshipOffer[]> {
    return this.client.get(
      `/api/billing/sponsorships/offers/mine${opts.includeDeferred ? '?includeDeferred=true' : ''}`
    );
  }

  /** Citizen: "Not now" on an offer — it stays listed, quiet until the window lapses. */
  async deferSponsorshipOffer(sponsorshipId: string): Promise<void> {
    await this.client.post(`/api/billing/sponsorships/${sponsorshipId}/defer`, {});
  }

  /**
   * Citizen: load a sponsorship offer the citizen has been emailed
   * about. Asserts the citizen owns the sponsored journey.
   */
  async getSponsorshipOffer(sponsorshipId: string): Promise<SponsorshipOfferDetails> {
    return this.client.get(`/api/billing/sponsorships/${sponsorshipId}/offer`);
  }

  /**
   * Citizen: accept a sponsorship offer. Flips the journey to
   * `sponsored_b2b`.
   */
  async acceptSponsorshipOffer(sponsorshipId: string): Promise<{
    id: string;
    status: string;
    talentAcceptedAt: string;
  }> {
    return this.client.post(`/api/billing/sponsorships/${sponsorshipId}/accept`, {});
  }

  /**
   * Citizen: decline a sponsorship offer. Triggers an automatic full
   * refund to the company.
   */
  async declineSponsorshipOffer(
    sponsorshipId: string,
    input: { reason?: string } = {}
  ): Promise<{
    sponsorship: { id: string; status: string };
    /** `null` when there was nothing to refund (a zero-amount sponsorship). */
    refundId: string | null;
  }> {
    return this.client.post(`/api/billing/sponsorships/${sponsorshipId}/decline`, input);
  }

  /**
   * Verify a completed Stripe Checkout Session and process it server-side.
   * Called after redirect as a reliable fallback for webhook processing.
   */
  async verifyCheckoutSession(sessionId: string): Promise<{
    success: boolean;
    operation?: string;
    journeyId?: string;
    alreadyProcessed?: boolean;
    reason?: string;
    error?: string;
  }> {
    return this.client.post('/api/billing/verify-session', { sessionId });
  }

  // ─────────────────────────────────────────────────────────────────────
  // INVOICES
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Get invoices
   */
  async getInvoices(params: InvoicePage): Promise<{ invoices: Invoice[]; total: number }> {
    return this.client.get<{ invoices: Invoice[]; total: number }>('/api/billing/invoices', {
      params: { limit: params.limit, offset: params.offset },
    });
  }

  /**
   * A FRESH link to an invoice's document, minted by Stripe for this open.
   * Open it now; never store it (Stripe's links expire).
   */
  async getInvoiceDocument(invoiceId: string): Promise<{ url: string; kind: InvoiceDocumentKind }> {
    return this.client.get<{ url: string; kind: InvoiceDocumentKind }>(
      `/api/billing/invoices/${encodeURIComponent(invoiceId)}/document`
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // CERTIFIED INVOICING
  // ─────────────────────────────────────────────────────────────────────

  /** The citizen's own billing profile — `null` when they have none. */
  async getBillingProfile(): Promise<{ profile: BillingProfile | null }> {
    return this.client.get<{ profile: BillingProfile | null }>('/api/billing/profile');
  }

  /** Create or replace the citizen's billing profile. */
  async saveBillingProfile(input: BillingProfileInput): Promise<{ profile: BillingProfile }> {
    return this.client.put<{ profile: BillingProfile }>('/api/billing/profile', input);
  }

  /** The company's billing details (any member of it). */
  async getCompanyBillingDetails(): Promise<{ details: CompanyBillingDetails }> {
    return this.client.get<{ details: CompanyBillingDetails }>('/api/billing/company-details');
  }

  /** Replace the company's billing details (a business admin). */
  async saveCompanyBillingDetails(
    input: CompanyBillingDetailsInput
  ): Promise<{ details: CompanyBillingDetails }> {
    return this.client.put<{ details: CompanyBillingDetails }>(
      '/api/billing/company-details',
      input
    );
  }

  /** Whether a checkout would be refused now, and what is missing. */
  async getFiscalReadiness(): Promise<FiscalReadiness> {
    return this.client.get<FiscalReadiness>('/api/billing/fiscal/readiness');
  }

  /** Tonnie team only — the reconciliation across every tenant. */
  async getFiscalReconciliation(): Promise<FiscalReconciliation> {
    return this.client.get<FiscalReconciliation>('/api/billing/fiscal/reconciliation');
  }

  /** Tonnie team only — queue a payment's document again. */
  async retryFiscalDocument(invoiceId: string): Promise<void> {
    await this.client.post<void>(
      `/api/billing/fiscal/invoices/${encodeURIComponent(invoiceId)}/retry`,
      {}
    );
  }

  /** Tonnie team only — a refund's credit note was issued in Jasmin. */
  async markCreditNoteIssued(invoiceId: string): Promise<void> {
    await this.client.post<void>(
      `/api/billing/fiscal/invoices/${encodeURIComponent(invoiceId)}/credit-note-issued`,
      {}
    );
  }

  // ─────────────────────────────────────────────────────────────────────
  // JOURNEY UNLOCK & RENEWAL
  // ─────────────────────────────────────────────────────────────────────

  /**
   * Create a Stripe Checkout session to unlock a journey instance (one-time payment).
   * Returns a checkout URL to redirect the user to.
   */
  async unlockJourney(params: {
    journeyId: string;
    successUrl: string;
    cancelUrl: string;
    /** Optional affiliate coupon code (BL-213). */
    couponCode?: string;
  }): Promise<JourneyUnlockResult> {
    return this.client.post<JourneyUnlockResult>('/api/billing/journey/unlock', params);
  }

  /**
   * Instantly unlock a journey for Pro subscribers (no Stripe payment).
   * Only works when the user has an active Pro subscription.
   */
  async unlockJourneyPro(journeyId: string): Promise<ProJourneyUnlockResult> {
    return this.client.post<ProJourneyUnlockResult>('/api/billing/journey/unlock-pro', {
      journeyId,
    });
  }

  /**
   * Create a Stripe Checkout session to renew a journey instance (one-time payment).
   * Returns a checkout URL to redirect the user to.
   */
  async renewJourney(params: {
    journeyId: string;
    successUrl: string;
    cancelUrl: string;
    /** Optional affiliate coupon code (BL-213). */
    couponCode?: string;
  }): Promise<JourneyRenewalResult> {
    return this.client.post<JourneyRenewalResult>('/api/billing/journey/renew', params);
  }

  /**
   * Cancel family add-on subscription
   */
  async cancelFamilyAddon(): Promise<{ success: boolean; effectiveDate: string }> {
    return this.client.post<{ success: boolean; effectiveDate: string }>(
      '/api/billing/subscription/cancel-family'
    );
  }
}

export function createBillingService(client: TonnieClient): BillingService {
  return new BillingService(client);
}
